import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  PencilIcon, TrashIcon, MagnifyingGlassIcon,
  PlayIcon, EyeIcon, HeartIcon, ShareIcon, PlusIcon,
} from "@heroicons/react/24/outline";
import LoadingSpinner from "../../../shared/components/LoadingSpinner";
import { useContentsManagement } from "../hooks/useContentsManagement";
import ContentEditModal from "../components/ContentEditModal";
import VideoPlayerModal from "../components/VideoPlayerModal";
import { getVideoData, getCategoryColor } from "../utils/content.utils";
import { useLanguage } from "../../../context/LanguageContext";

export default function ContentsManagement() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const {
    loading, filtered, searchTerm, setSearchTerm,
    editingContent, setEditingContent, showEditModal, openEdit, closeEdit, save, saving,
    uploadProgress, uploadError,
    selectedVideo, setSelectedVideo, remove,
  } = useContentsManagement();

  const [confirmId, setConfirmId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!confirmId) return;
    setDeleting(true);
    try { await remove(confirmId); } finally { setDeleting(false); setConfirmId(null); }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 pt-24 pb-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">{t("admin.contentsManagement.title")}</h1>
            <p className="text-gray-600">{t("admin.contentsManagement.subtitle")}</p>
          </div>
          <button onClick={() => navigate("/admin/contents/new")}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-[#b2d12e] to-[#a0c827] text-gray-900 px-6 py-3 rounded-lg font-semibold hover:shadow-lg hover:-translate-y-0.5 transition-all">
            <PlusIcon className="w-5 h-5" /> {t("admin.contentsManagement.addContent")}
          </button>
        </div>

        {/* Search */}
        <div className="mb-8">
          <div className="relative bg-white rounded-xl shadow-sm border border-gray-200 focus-within:shadow-md focus-within:border-[#b2d12e] transition-all">
            <MagnifyingGlassIcon className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input className="w-full pl-12 pr-4 py-3 text-gray-700 placeholder-gray-400 rounded-xl border-0 focus:outline-none"
              placeholder={t("admin.contentsManagement.searchPlaceholder")}
              value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
          </div>
        </div>

        {/* Grid / Empty */}
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24">
            <MagnifyingGlassIcon className="w-16 h-16 text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">{t("admin.contentsManagement.noContentFound")}</h3>
            <p className="text-gray-600 mb-6">{searchTerm ? t("admin.contentsManagement.tryAdjustingSearch") : t("admin.contentsManagement.startAdding")}</p>
            <button onClick={() => navigate("/admin/contents/new")}
              className="inline-flex items-center gap-2 bg-[#b2d12e] text-gray-900 px-6 py-2 rounded-lg font-semibold hover:shadow-md transition-all">
              <PlusIcon className="w-5 h-5" /> {t("admin.contentsManagement.addFirstContent")}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((c) => {
              const video = getVideoData(c);
              const color = getCategoryColor(typeof c.category === "string" ? c.category : (c.category as any)?.name ?? "");
              return (
                <div key={c.id}
                  className="group bg-white rounded-xl shadow-sm hover:shadow-xl border border-gray-100 hover:border-gray-200 transition-all hover:-translate-y-1 overflow-hidden">
                  {/* Thumbnail */}
                  <div className="relative h-40 bg-gray-900 overflow-hidden">
                    {video ? (
                      <div onClick={() => setSelectedVideo(video)}
                        className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-gray-900 to-black cursor-pointer hover:opacity-80 transition-opacity gap-2">
                        <div className="bg-[#b2d12e]/20 p-4 rounded-full group-hover:bg-[#b2d12e]/40 transition-all">
                          <PlayIcon className="w-8 h-8 text-[#b2d12e]" />
                        </div>
                        <span className="text-sm text-gray-300">{t("admin.contentsManagement.clickToPlay")}</span>
                      </div>
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center gap-2">
                        <PlayIcon className="w-12 h-12 text-gray-700" />
                        <span className="text-sm text-gray-500">{t("admin.contentsManagement.noVideo")}</span>
                      </div>
                    )}
                  </div>

                  <div className="p-5">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold border mb-3 ${color.bg} ${color.text} ${color.border}`}>
                      {typeof c.category === "string" ? c.category : (c.category as any)?.name}
                    </span>
                    <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-[#b2d12e] transition-colors">{c.title}</h3>
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2 h-10">{c.description}</p>

                    {/* Metrics */}
                    <div className="grid grid-cols-3 gap-2 mb-4 py-3 border-y border-gray-100">
                      {[
                        { icon: <EyeIcon className="w-4 h-4" />, value: c.metrics?.views ?? 0, label: t("admin.contentsManagement.views"), color: "text-gray-600" },
                        { icon: <HeartIcon className="w-4 h-4" />, value: c.metrics?.likes ?? 0, label: t("admin.contentsManagement.likes"), color: "text-red-500" },
                        { icon: <ShareIcon className="w-4 h-4" />, value: c.metrics?.shares ?? 0, label: t("admin.contentsManagement.shares"), color: "text-blue-500" },
                      ].map((m) => (
                        <div key={m.label} className="text-center">
                          <div className={`flex items-center justify-center mb-1 ${m.color}`}>{m.icon}</div>
                          <span className="text-sm font-bold text-gray-900">{m.value}</span>
                          <p className="text-xs text-gray-500">{m.label}</p>
                        </div>
                      ))}
                    </div>

                    <div className="flex gap-3">
                      <button onClick={() => openEdit(c)}
                        className="flex-1 flex items-center justify-center gap-2 bg-blue-50 hover:bg-blue-100 text-blue-600 py-2 rounded-lg font-medium transition-colors">
                        <PencilIcon className="w-4 h-4" /><span className="hidden sm:inline">{t("common.edit")}</span>
                      </button>
                      <button onClick={() => setConfirmId(Number(c.id))}
                        className="flex-1 flex items-center justify-center gap-2 bg-red-50 hover:bg-red-100 text-red-600 py-2 rounded-lg font-medium transition-colors">
                        <TrashIcon className="w-4 h-4" /><span className="hidden sm:inline">{t("common.delete")}</span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showEditModal && editingContent && (
        <ContentEditModal
          content={editingContent}
          onChange={setEditingContent}
          onSave={save}
          onClose={closeEdit}
          saving={saving}
          uploadProgress={uploadProgress}
          uploadError={uploadError}
        />
      )}

      {selectedVideo && (
        <VideoPlayerModal video={selectedVideo} onClose={() => setSelectedVideo(null)} />
      )}

      {confirmId !== null && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm mx-4">
            <h3 className="text-base font-bold text-gray-900 mb-2">{t("admin.contentsManagement.deleteConfirmTitle")}</h3>
            <p className="text-sm text-gray-500 mb-5">{t("common.thisActionCannotBeUndone")}</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmId(null)} disabled={deleting}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50">
                {t("common.cancel")}
              </button>
              <button onClick={handleDelete} disabled={deleting}
                className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-xl text-sm font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                {deleting && <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                {deleting ? t("common.deleting") : t("common.delete")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
